/**
 * EntityWorkReportsSection — "Rapporte & Aufwand" for one entity
 * (D1 tasks, D2a tenants).
 *
 * Answers, at a glance: how much time has this cost, who worked on it,
 * and what was actually done.
 *
 * Three KPIs come from a server-side aggregate over EVERY linked report
 * (GET /<entity>/{id}/work-report-summary) — never from the page the
 * list happens to have loaded, which would understate a long-running
 * entity. The rows come from the ordinary list endpoint filtered by the
 * SAME direct foreign key, in the backend's own order (work_date DESC,
 * started_at DESC, id DESC).
 *
 * The only per-entity knobs are strings (heading, empty text, a11y
 * names) and which columns to show — no behavioural branching, so the
 * task and tenant views cannot drift apart.
 *
 * Reuses the shared work-report vocabulary (formatMinutes,
 * formatWorkDate, REPORT_TYPE_LABELS, isoToTimeInputValue) rather than
 * inventing a second one, and delegates create/edit/delete entirely to
 * the caller's WorkReportFormModal + WorkReportCancelDialog — there is
 * exactly one CRUD path for reports in the app.
 *
 * Layout follows Pattern 2 (RESPONSIVE_PATTERNS.md): a real <table> from
 * md upwards, a card stack below. Theming is token-only so light and
 * dark both work; the amber duration badge is the established
 * report-time accent and is kept.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ban, Pencil } from "lucide-react";

import {
  REPORT_TYPE_LABELS,
  formatMinutes,
  formatWorkDate,
  getWorkReportDetailPath,
  isoToTimeInputValue,
  openReportLabel,
} from "../../calendar/workReports/workReportConstants";
import { useCategoryDisplay } from "../../activityCategories/ActivityCategoryChip";

/** Where a row opens (D2f). Reading a report is no longer an act of
 * editing it — the row navigates, the pencil still opens the form.
 *
 * D2f.1: delegates to the shared path builder, so this section and the
 * calendar cannot spell the route differently. Null for a row without
 * an id — the caller then renders text instead of a dead link. */
export function reportDetailPath(report) {
  return getWorkReportDetailPath(report?.id);
}

/**
 * Should a click anywhere in the row navigate?
 *
 * No, if it started on something that already has its own behaviour —
 * the title link, the edit/delete buttons, a future menu, or selected
 * text the user is trying to copy. Everything else (the date, time,
 * duration, assignee and type cells) is dead space that ought to open
 * the report, which is what "the row is not clickable" was about.
 */
export function shouldRowNavigate(event) {
  if (event.defaultPrevented) return false;
  // Let the browser handle modified clicks (new tab / new window).
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  const target = event.target;
  if (target?.closest?.("a, button, input, select, textarea, [role='button']")) {
    return false;
  }
  const selection = window.getSelection?.();
  if (selection && String(selection).length > 0) return false;
  return true;
}

/** "Selina Ieri" → e-mail → an honest placeholder. Never a UUID. */
export function assigneeLabel(report) {
  return (
    (report?.assigned_to_full_name || "").trim() ||
    (report?.assigned_to_email || "").trim() ||
    "Unbekannter Mitarbeiter"
  );
}

/**
 * The row's headline. WorkReport HAS a required title, so it is always
 * the primary text; the type label is only a fallback for a legacy row
 * that somehow carries an empty one.
 */
export function reportHeadline(report) {
  const title = (report?.title || "").trim();
  if (title) return title;
  return REPORT_TYPE_LABELS[report?.report_type] || report?.report_type || "Rapport";
}

/**
 * "08:00–10:30" when both ends are present, else null. The list already
 * shows the duration separately, so a half range would add noise.
 */
export function timeRangeLabel(report) {
  const from = isoToTimeInputValue(report?.started_at);
  const to = isoToTimeInputValue(report?.ended_at);
  if (!from || !to) return null;
  return `${from}–${to}`;
}

/**
 * Compact object context, only from fields the list response already
 * carries (batch-enriched server-side, so no request per row) and never
 * a raw id — an unresolved link is simply omitted.
 *
 * ``exclude`` drops the field that IS the page: on a tenant's own tab
 * every row would otherwise repeat that tenant's name as "context",
 * which is noise. Each scope therefore has its own preference order,
 * always skipping itself. Only three display fields exist on the list
 * item (tenant name, unit short id, task title), so scopes without one
 * of their own simply fall through to what is there.
 */
const CONTEXT_FIELDS = {
  tenant: ["task_title", "unit_short_id"],
  unit: ["tenant_display_name", "task_title"],
  task: ["tenant_display_name", "unit_short_id"],
  tenancy: ["tenant_display_name", "unit_short_id"],
  application: ["tenant_display_name", "unit_short_id"],
  landlord: ["unit_short_id", "task_title"],
  // D4c3. These two collect work from one level below them, so the most
  // useful context is WHERE inside them it happened: which room, else
  // which unit. Both skip their own name, like every entry above.
  property: ["room_display_name", "unit_short_id", "task_title"],
  room: ["unit_short_id", "tenant_display_name", "task_title"],
};

export function linkedLabel(report, { exclude } = {}) {
  const fields =
    CONTEXT_FIELDS[exclude] || CONTEXT_FIELDS.task;
  return (
    fields.map((f) => (report?.[f] || "").trim()).find(Boolean) || null
  );
}

/** "2 Stunden 30 Minuten" — spoken form for screen readers, because
 * "2h 30m" is read out as letters. The visible text stays the app-wide
 * formatMinutes output so this section matches the calendar. */
export function spokenDuration(minutes) {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.trunc(minutes)) : 0;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  const parts = [];
  if (h > 0) parts.push(`${h} ${h === 1 ? "Stunde" : "Stunden"}`);
  if (rest > 0 || h === 0) {
    parts.push(`${rest} ${rest === 1 ? "Minute" : "Minuten"}`);
  }
  return parts.join(" ");
}

/** "0 Personenbuchungen" / "1 Personenbuchung" / "5 Personenbuchungen".
 *
 * Same rename as ``formatReportCount`` and for the same reason: the
 * number is still one per report ROW, and a row is one person's
 * booking. Section headings stay "Rapporte" — it is the COUNT that
 * would be misread, not the noun for the things being listed. */
export function reportCountLabel(count) {
  const n = Number.isFinite(count) ? count : 0;
  return `${n} ${n === 1 ? "Personenbuchung" : "Personenbuchungen"}`;
}

const KPI_CARD =
  "rounded-[8px] border border-border bg-muted/40 px-3 py-2 " +
  "dark:border-[#1e2130] dark:bg-[#0d0f14]";
const KPI_LABEL =
  "m-0 text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]";
const KPI_VALUE =
  "m-0 mt-0.5 text-[15px] font-bold tabular-nums text-foreground dark:text-[#e2e4ed]";

const ICON_BUTTON =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] " +
  "border border-border bg-transparent text-muted-foreground " +
  "hover:bg-muted hover:text-foreground focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-ring " +
  "dark:border-[#1e2130] dark:hover:bg-[#13161f]";

const DURATION_BADGE =
  "inline-flex shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 " +
  "text-[10px] font-bold text-amber-700 dark:text-amber-400";

/**
 * The report's area of work (CAT1), as a chip.
 *
 * Label and colour both come from the ONE category table — never a raw
 * code, and never a colour without its word next to it, so the chip
 * carries its meaning for anyone who cannot tell the hues apart.
 *
 * An unset category is shown as plain muted text rather than a
 * colourless chip: on a page of pre-CAT1 rows a chip per row would be
 * noise, and "Nicht zugewiesen" is a state, not a category. An unknown
 * code lands here too — ``activityCategory`` returns null for it — which
 * is the same thing the detail page does with one.
 */
export function CategoryChip({ code }) {
  // RESP2d: resolved through the organisation's catalog, so a renamed
  // area reads correctly here without any stored row changing. One
  // shared cache backs every chip on the page — see
  // activityCategoryCatalogStore for why this is not a fetch per row.
  const { state, label, colorToken, isInactive } = useCategoryDisplay(code);
  if (state !== "known") {
    return (
      <span className="text-muted-foreground dark:text-[#4a5070]">
        {label}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${colorToken}`}
    >
      {label}
      {isInactive ? <span className="ml-1 font-normal">· Inaktiv</span> : null}
    </span>
  );
}

/**
 * "Storniert".
 *
 * This list does not ask for cancelled reports — it sends no
 * ``include_cancelled``, so today none arrive and this renders nothing.
 * It exists because ``is_cancelled`` IS on the row: the day anything
 * lets one through, a struck-out report must say so rather than sit
 * among the active ones looking like worked time. Text, not colour
 * alone — same badge the calendar list uses.
 */
function CancelledBadge({ report }) {
  if (!report?.is_cancelled) return null;
  return (
    <span className="inline-flex shrink-0 rounded-full border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-bold text-destructive">
      Storniert
    </span>
  );
}

function KpiCard({ label, value, valueLabel }) {
  return (
    <div className={KPI_CARD}>
      <p className={KPI_LABEL}>{label}</p>
      <p className={KPI_VALUE} aria-label={valueLabel}>
        {value}
      </p>
    </div>
  );
}

/**
 * Edit and delete.
 *
 * ``z-10`` lifts the buttons above the card's stretched link overlay,
 * and both handlers stop the event before it can reach an ancestor —
 * the pencil opens the modal and nothing else. Without that, tapping
 * edit on a phone would open the form AND navigate away behind it.
 */
function RowActions({ report, onEdit, onDelete }) {
  const headline = reportHeadline(report);
  // PICK1c: a cancelled report is read-only — editing it answers 409
  // (work_report_service.py:798-802) and cancelling it again answers 409
  // too, so offering either button only promises something the server
  // refuses. The row itself stays a link: the report is preserved as
  // evidence and must remain readable, badge and all.
  if (report?.is_cancelled) return null;
  const only = (fn) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    fn(report);
  };
  return (
    <div className="relative z-10 flex items-center justify-end gap-1">
      <button
        type="button"
        onClick={only(onEdit)}
        aria-label={`Rapport bearbeiten: ${headline}`}
        className={ICON_BUTTON}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {onDelete ? (
        <button
          type="button"
          onClick={only(onDelete)}
          aria-label={`Rapport stornieren: ${headline}`}
          className={ICON_BUTTON}
        >
          <Ban className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export default function EntityWorkReportsSection({
  workReports,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  /** One of WORK_REPORT_SCOPES — only affects wording and which context
   * column is shown; the data path is identical for all of them. */
  scope = "task",
  headingId = "entity-reports-heading",
  /** D4c3: only the scopes that live on a page WITHOUT a "Rapporte" tab
   * to name them pass this. Everywhere else the generic heading stays. */
  headingText = "Rapporte & Aufwand",
  emptyText = "Für diese Aufgabe wurden noch keine Rapporte erfasst.",
  emptyHint = null,
  captionText = "Rapporte, die mit dieser Aufgabe verknüpft sind",
  /** Rendered inside the section header. The task page keeps its create
   * action in the page header instead and passes nothing. */
  headerAction = null,
  /** TASK-UX1: opt OUT of the KPI row when the caller already shows the
   * same aggregate somewhere else — the task workspace moved it into the
   * "Zeiterfassung" sidebar card, and one total printed twice on one
   * screen is one total too many. Default keeps every other call site
   * (tenant, unit, landlord …) exactly as it was. The summary error and
   * its retry travel WITH the numbers: hiding the KPIs while leaving
   * their error behind would report a failure next to nothing. */
  showSummary = true,
}) {
  const {
    reports,
    total,
    summary,
    loading,
    loadingMore,
    error,
    summaryError,
    loadMoreError,
    hasMore,
    retry,
    retrySummary,
    loadMore,
  } = workReports;

  const navigate = useNavigate();

  /** Mouse convenience for the desktop table: a <tr> cannot be an
   * anchor, and `position: relative` on a table row is unreliable, so
   * the whole-row hit area is a click handler. The KEYBOARD path stays
   * the real <Link> in the title cell — this handler adds nothing a
   * keyboard user needs, so no role or tabIndex is invented here. */
  const openReport = (report) => (event) => {
    if (!shouldRowNavigate(event)) return;
    const path = reportDetailPath(report);
    if (path) navigate(path);
  };

  const isEmpty = !loading && !error && reports.length === 0;
  const totalMinutes = summary?.total_minutes ?? 0;
  // Each scope hides its own identity from the row context (see
  // CONTEXT_FIELDS) — repeating the page's subject on every row is noise.
  const contextOf = (r) => linkedLabel(r, { exclude: scope });

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]"
    >
      {/* The task page carries "+ Rapport erfassen" in its page header
          (C1.1, sticky on mobile) and passes no headerAction, so there
          is no duplicate there. The tenant tab has no such header, so it
          passes one. Either way the empty state offers the action too —
          there it is a call to action, not a repeat. */}
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2
          id={headingId}
          className="m-0 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
        >
          {headingText}
        </h2>
        {headerAction}
      </header>

      {/* KPIs — from a server-side aggregate over every linked report,
          not from the rows below. */}
      {showSummary ? (
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {/* REPORT-PEOPLE: see TaskTimeCard — same number, honest
              name. The heading above still says "Rapporte & Aufwand";
              it is the COUNT and the SUM that would be misread, not the
              noun for the things being listed. */}
          <KpiCard
            label="Personenzeit"
            value={summary ? formatMinutes(totalMinutes) : "–"}
            valueLabel={
              summary
                ? `Personenzeit: ${spokenDuration(totalMinutes)}`
                : undefined
            }
          />
          <KpiCard
            label="Personenbuchungen"
            value={summary ? reportCountLabel(summary.report_count) : "–"}
          />
          <KpiCard
            label="Letzter Rapport"
            value={
              summary
                ? summary.last_report_date
                  ? formatWorkDate(summary.last_report_date)
                  : "Noch kein Rapport"
                : "–"
            }
          />
        </div>
      ) : null}

      {showSummary && summaryError ? (
        <div
          role="alert"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive"
        >
          <span>{summaryError}</span>
          <button
            type="button"
            onClick={retrySummary}
            className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={retry}
            className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {loading && reports.length === 0 ? (
        <p
          role="status"
          className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]"
        >
          Rapporte werden geladen …
        </p>
      ) : null}

      {isEmpty ? (
        <div
          role="status"
          className="rounded-[8px] border border-dashed border-border px-3 py-6 text-center dark:border-[#1e2130]"
        >
          <p className="m-0 text-[13px] text-muted-foreground dark:text-[#4a5070]">
            {emptyText}
          </p>
          {emptyHint ? (
            <p className="m-0 mt-1 text-[11px] text-muted-foreground dark:text-[#4a5070]">
              {emptyHint}
            </p>
          ) : null}
          <button
            type="button"
            onClick={onCreateClick}
            className="mt-3 rounded-[8px] bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
          >
            + Rapport erfassen
          </button>
        </div>
      ) : null}

      {reports.length > 0 ? (
        <>
          {/* Mobile: card stack */}
          <ul className="m-0 list-none space-y-2 p-0 md:hidden">
            {reports.map((r) => (
              /* The whole card is tappable via ONE stretched link on the
                 headline (`after:absolute after:inset-0`) rather than an
                 onClick on the <li>: it is a real anchor, so it is
                 focusable, Enter works natively and middle-click opens a
                 tab — none of which a clickable div gives you. The
                 action buttons sit above the overlay via z-10. */
              <li
                key={r.id}
                className="relative rounded-[8px] border border-border bg-background p-3 transition-colors hover:bg-muted/40 dark:border-[#1e2130] dark:bg-[#0d0f14] dark:hover:bg-[#13161f]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="m-0 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="font-semibold tabular-nums text-muted-foreground dark:text-[#4a5070]">
                        {formatWorkDate(r.work_date)}
                      </span>
                      <span
                        className={DURATION_BADGE}
                        aria-label={spokenDuration(r.duration_minutes)}
                      >
                        {formatMinutes(r.duration_minutes)}
                      </span>
                      <CancelledBadge report={r} />
                    </p>
                    <p className="m-0 mt-1 text-[13px] font-semibold">
                      {reportDetailPath(r) ? (
                        <Link
                          to={reportDetailPath(r)}
                          aria-label={openReportLabel(reportHeadline(r))}
                          title={reportHeadline(r)}
                          className="block truncate rounded-[4px] text-foreground after:absolute after:inset-0 after:content-[''] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-[#c5cbe0]"
                        >
                          {reportHeadline(r)}
                        </Link>
                      ) : (
                        /* No id — render the text, never a link to
                           /admin/work-reports/undefined. */
                        <span
                          className="block truncate text-foreground dark:text-[#c5cbe0]"
                          title={reportHeadline(r)}
                        >
                          {reportHeadline(r)}
                        </span>
                      )}
                    </p>
                    <p className="m-0 mt-0.5 text-[11px] text-muted-foreground dark:text-[#4a5070]">
                      {[
                        assigneeLabel(r),
                        REPORT_TYPE_LABELS[r.report_type] || r.report_type,
                        timeRangeLabel(r),
                        contextOf(r),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {/* D4c3: its own line, not appended to the meta
                        string above — the type and the area of work are
                        different questions ("Übergabe" vs "das Schreiben
                        des Protokolls"), and running them together in
                        one dot-separated run reads as one taxonomy. */}
                    <p className="m-0 mt-1 text-[11px]">
                      <CategoryChip code={r.activity_category} />
                    </p>
                  </div>
                  <RowActions
                    report={r}
                    onEdit={onEditClick}
                    onDelete={onDeleteClick}
                  />
                </div>
              </li>
            ))}
          </ul>

          {/* Tablet/Desktop: table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-[12px]">
              <caption className="sr-only">
                {captionText}
              </caption>
              <thead>
                <tr className="border-b border-border text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground dark:border-[#1e2130] dark:text-[#4a5070]">
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Datum
                  </th>
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Zeit
                  </th>
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Dauer
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    Tätigkeit
                  </th>
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Mitarbeiter
                  </th>
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Typ
                  </th>
                  <th scope="col" className="py-2 pr-3 whitespace-nowrap">
                    Tätigkeitsbereich
                  </th>
                  <th scope="col" className="py-2 text-right whitespace-nowrap">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    onClick={openReport(r)}
                    className="cursor-pointer border-b border-border/60 last:border-b-0 hover:bg-muted/50 dark:border-[#1e2130]/60 dark:hover:bg-[#0d0f14]/60"
                  >
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-foreground dark:text-[#c5cbe0]">
                      {formatWorkDate(r.work_date)}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-muted-foreground dark:text-[#4a5070]">
                      {timeRangeLabel(r) || "–"}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={DURATION_BADGE}
                          aria-label={spokenDuration(r.duration_minutes)}
                        >
                          {formatMinutes(r.duration_minutes)}
                        </span>
                        <CancelledBadge report={r} />
                      </span>
                    </td>
                    {/* The headline is the row's LINK — the keyboard and
                        screen-reader path. The surrounding <tr> carries a
                        click handler so the date, time, duration,
                        assignee and type cells open the report too
                        (D2f.1); a <tr> cannot be an anchor and
                        `position: relative` on a table row is unreliable,
                        so a stretched overlay is not an option here. */}
                    <td className="max-w-[280px] py-2 pr-3">
                      {reportDetailPath(r) ? (
                        <Link
                          to={reportDetailPath(r)}
                          aria-label={openReportLabel(reportHeadline(r))}
                          className="block truncate rounded-[4px] font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-[#c5cbe0]"
                          title={reportHeadline(r)}
                        >
                          {reportHeadline(r)}
                        </Link>
                      ) : (
                        <span
                          className="block truncate font-medium text-foreground dark:text-[#c5cbe0]"
                          title={reportHeadline(r)}
                        >
                          {reportHeadline(r)}
                        </span>
                      )}
                      {contextOf(r) ? (
                        <span className="block truncate text-[11px] text-muted-foreground dark:text-[#4a5070]">
                          {contextOf(r)}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap text-foreground dark:text-[#c5cbe0]">
                      {assigneeLabel(r)}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground dark:text-[#4a5070]">
                      {REPORT_TYPE_LABELS[r.report_type] || r.report_type}
                    </td>
                    <td className="py-2 pr-3 text-[11px]">
                      <CategoryChip code={r.activity_category} />
                    </td>
                    <td className="py-2 text-right">
                      <RowActions
                        report={r}
                        onEdit={onEditClick}
                        onDelete={onDeleteClick}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loadMoreError ? (
            <div
              role="alert"
              className="mt-2 flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive"
            >
              <span>{loadMoreError}</span>
              <button
                type="button"
                onClick={loadMore}
                className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Erneut versuchen
              </button>
            </div>
          ) : null}

          {hasMore ? (
            <div className="mt-2 flex items-center justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:border-[#1e2130] dark:hover:bg-[#0d0f14]"
              >
                {loadingMore
                  ? "Lädt …"
                  : `Mehr laden (${reports.length} von ${total})`}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
